/**
 * Native Location Utilities
 * 
 * Centralises all native location permission checking, requesting,
 * settings deep-linking, and background geolocation.
 * 
 * BACKGROUND TRACKING ARCHITECTURE:
 * - Uses @capacitor-community/background-geolocation for true background +
 *   locked-screen tracking on BOTH iOS and Android.
 * - On Android: the plugin automatically runs a foreground service with a
 *   persistent notification (configured via backgroundTitle/backgroundMessage).
 * - On iOS: the plugin uses CLLocationManager with allowsBackgroundLocationUpdates,
 *   which works with backgroundModes: ['location'] in capacitor.config.ts.
 * - Falls back to @capacitor/geolocation for permission checks and single reads.
 * 
 * PERMISSION MAPPING (iOS):
 * - @capacitor/geolocation maps BOTH "whenInUse" and "always" → "granted"
 * - We cannot distinguish the two from JS alone
 * - We detect background capability by attempting a BG watcher start
 * - If it fails with a permission error, we know "Always" is not granted
 */

import { emitTrackingDiag, categoriseTrackingError } from "@/lib/trackingDiagnostics";
import { Capacitor } from "@capacitor/core";
import { registerPlugin } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { App } from "@capacitor/app";
import type {
  BackgroundGeolocationPlugin,
  CallbackError,
  Location as BGLocation,
} from "@capacitor-community/background-geolocation";

// Register the background geolocation plugin
const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>(
  "BackgroundGeolocation",
);

// ─── Types ───────────────────────────────────────────────────────────────────

export type LocationPermissionLevel = "granted" | "denied" | "prompt" | "limited" | "unknown";

export interface LocationPermissionInfo {
  /** Effective permission level (granted = whenInUse OR always on iOS) */
  permission: LocationPermissionLevel;
  /** Whether the OS can show the native prompt again */
  canPromptAgain: boolean;
  /** iOS-only: whether precise location is on */
  preciseLocation: boolean;
}

// ─── Platform helpers ────────────────────────────────────────────────────────

const isNative = () => Capacitor.isNativePlatform();
const platform = () => Capacitor.getPlatform(); // "ios" | "android" | "web"

// ─── Permission checking ─────────────────────────────────────────────────────

let _checkInFlight = false;
let _cachedPermission: LocationPermissionInfo | null = null;
let _cachedAt = 0;
const PERMISSION_CACHE_MS = 3_000;

/**
 * Non-blocking permission check. Returns current state without prompting.
 * 
 * IMPORTANT: On iOS, "granted" means either "When In Use" OR "Always".
 * Use `testBackgroundCapability()` to check if background tracking works.
 */
export async function checkLocationPermission(skipCache = false): Promise<LocationPermissionInfo> {
  if (!isNative()) {
    return { permission: "unknown", canPromptAgain: false, preciseLocation: true };
  }

  // Return cached result if fresh
  if (!skipCache && _cachedPermission && (Date.now() - _cachedAt) < PERMISSION_CACHE_MS) {
    return _cachedPermission;
  }

  if (_checkInFlight) {
    if (_cachedPermission) return _cachedPermission;
    return { permission: "unknown", canPromptAgain: false, preciseLocation: true };
  }

  _checkInFlight = true;
  try {
    const status = await Geolocation.checkPermissions();
    const loc = (status.location || "prompt") as LocationPermissionLevel;
    const coarse = (status.coarseLocation || "prompt") as string;

    console.log("[NativeLocation] LOCATION_STATUS_CHECK:", { location: loc, coarse, platform: platform() });

    const canPromptAgain = loc === "prompt";
    const preciseLocation = !(coarse === "granted" && loc !== "granted");

    const result: LocationPermissionInfo = { permission: loc, canPromptAgain, preciseLocation };
    _cachedPermission = result;
    _cachedAt = Date.now();

    console.log("[NativeLocation] LOCATION_STATUS_RESULT:", {
      permission: loc,
      canPromptAgain,
      preciseLocation,
    });

    return result;
  } catch (error) {
    console.error("[NativeLocation] checkPermissions error:", error);
    return { permission: "unknown", canPromptAgain: false, preciseLocation: true };
  } finally {
    _checkInFlight = false;
  }
}

/**
 * Invalidate the cached permission so the next check hits native.
 */
export function invalidatePermissionCache(): void {
  _cachedPermission = null;
  _cachedAt = 0;
}

// ─── iOS Permission Priming ──────────────────────────────────────────────────

let _didPrimeThisSession = false;

/**
 * One-time "prime" for iOS permission state.
 * 
 * On iOS, `Geolocation.checkPermissions()` can return "prompt" even when the
 * user has already granted "While Using" in Settings — until at least one
 * `requestPermissions()` call occurs in the current app session.
 * 
 * This function calls `requestPermissions()` exactly once per session when
 * `checkPermissions()` returns "prompt". If iOS has already been granted
 * permission, this resolves instantly without showing any system dialog.
 * 
 * Safe to call multiple times — only executes once per session.
 */
export async function primeLocationPermissionOnce(): Promise<LocationPermissionInfo> {
  if (!isNative()) {
    return { permission: "unknown", canPromptAgain: false, preciseLocation: true };
  }

  if (_didPrimeThisSession) {
    return checkLocationPermission();
  }

  _didPrimeThisSession = true;
  console.log("[NativeLocation] LOCATION_PERMISSION_PRIME_START");

  const info = await checkLocationPermission(true);

  if (info.permission !== "prompt") {
    console.log("[NativeLocation] LOCATION_PERMISSION_PRIME_DONE:", {
      status: info.permission,
      action: "no_prime_needed",
    });
    return info;
  }

  // Permission is "prompt" — call requestPermissions to prime iOS
  invalidatePermissionCache();

  try {
    const timeoutMs = 10_000;
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("PRIME_TIMEOUT")), timeoutMs),
    );

    await Promise.race([
      Geolocation.requestPermissions({ permissions: ['location'] }),
      timeoutPromise,
    ]);
  } catch (error: any) {
    console.warn("[NativeLocation] Prime request error/timeout:", error?.message);
  }

  invalidatePermissionCache();
  const result = await checkLocationPermission(true);

  console.log("[NativeLocation] LOCATION_PERMISSION_PRIME_DONE:", {
    status: result.permission,
    action: "primed",
  });

  return result;
}

// ─── Permission requesting ───────────────────────────────────────────────────

let _requestInFlight = false;

/**
 * Request location permission from the OS.
 * On iOS this requests "When In Use" first (iOS shows its own prompt).
 * To upgrade to "Always", the user must go to Settings.
 */
export async function requestLocationPermission(): Promise<LocationPermissionInfo> {
  if (!isNative()) {
    return { permission: "unknown", canPromptAgain: false, preciseLocation: true };
  }

  if (_requestInFlight) {
    console.warn("[NativeLocation] Permission request already in flight, skipping");
    return checkLocationPermission();
  }

  _requestInFlight = true;
  console.log("[NativeLocation] Requesting permission...");

  invalidatePermissionCache();

  try {
    const timeoutMs = 10_000;
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("LOCATION_REQUEST_TIMEOUT")), timeoutMs),
    );

    const status = await Promise.race([
      Geolocation.requestPermissions(),
      timeoutPromise,
    ]);

    invalidatePermissionCache();

    const loc = (status.location || "prompt") as LocationPermissionLevel;
    const coarse = (status.coarseLocation || "prompt") as string;
    const preciseLocation = !(coarse === "granted" && loc !== "granted");
    const canPromptAgain = loc === "prompt";

    console.log("[NativeLocation] LOCATION_PERMISSION_RESULT:", { permission: loc, preciseLocation });

    const result: LocationPermissionInfo = { permission: loc, canPromptAgain, preciseLocation };
    _cachedPermission = result;
    _cachedAt = Date.now();

    return result;
  } catch (error: any) {
    if (error?.message === "LOCATION_REQUEST_TIMEOUT") {
      console.warn("[NativeLocation] Permission request timed out after 10s");
      const current = await checkLocationPermission(true).catch(() => ({
        permission: "unknown" as LocationPermissionLevel,
        canPromptAgain: false,
        preciseLocation: true,
      }));
      return { ...current, _timedOut: true } as any;
    }
    console.error("[NativeLocation] requestPermissions error:", error);
    return { permission: "unknown", canPromptAgain: false, preciseLocation: true };
  } finally {
    _requestInFlight = false;
  }
}

// ─── Background capability test ──────────────────────────────────────────────

let _bgCapabilityCache: boolean | null = null;
let _bgCapabilityCacheAt = 0;
const BG_CAPABILITY_CACHE_MS = 30_000;

/**
 * Test whether background tracking can actually start.
 * This attempts to add a BG watcher and immediately removes it.
 * Returns true if the watcher started (= "Always" permission on iOS, 
 * or "Allow all the time" on Android), false otherwise.
 * 
 * Results are cached for 30s to avoid unnecessary native calls.
 */
export async function testBackgroundCapability(skipCache = false): Promise<boolean> {
  if (!isNative()) return false;

  if (!skipCache && _bgCapabilityCache !== null && (Date.now() - _bgCapabilityCacheAt) < BG_CAPABILITY_CACHE_MS) {
    return _bgCapabilityCache;
  }

  try {
    const timeoutMs = 5_000;
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("BG_CAPABILITY_TIMEOUT")), timeoutMs),
    );

    const testId = await Promise.race([
      BackgroundGeolocation.addWatcher(
        {
          backgroundTitle: "TransportMate",
          backgroundMessage: "Checking background capability",
          requestPermissions: false,
          stale: true,
          distanceFilter: 999999, // don't actually deliver updates
        },
        () => {}, // no-op callback
      ),
      timeoutPromise,
    ]);

    // If we got here, BG tracking is allowed
    await BackgroundGeolocation.removeWatcher({ id: testId });

    _bgCapabilityCache = true;
    _bgCapabilityCacheAt = Date.now();
    console.log("[NativeLocation] LOCATION_BACKGROUND_CAPABLE: true");
    return true;
  } catch (error) {
    _bgCapabilityCache = false;
    _bgCapabilityCacheAt = Date.now();
    console.log("[NativeLocation] LOCATION_BACKGROUND_CAPABLE: false", error);
    return false;
  }
}

/**
 * Invalidate background capability cache (e.g. after returning from Settings).
 */
export function invalidateBackgroundCapabilityCache(): void {
  _bgCapabilityCache = null;
  _bgCapabilityCacheAt = 0;
}

// ─── Open Settings ───────────────────────────────────────────────────────────

/**
 * Open the device's native app settings page.
 */
export async function openNativeAppSettings(): Promise<boolean> {
  const p = platform();
  console.log("[NativeLocation] LOCATION_SETTINGS_OPENED:", { platform: p });

  try {
    if (p === "ios") {
      try {
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url: "app-settings:", windowName: "_system" });
        console.log("[NativeLocation] Opened iOS settings via Browser._system");
        return true;
      } catch (browserError) {
        console.warn("[NativeLocation] Browser._system failed, trying window.open:", browserError);
        window.open("app-settings:", "_system");
        return true;
      }
    }

    if (p === "android") {
      try {
        await BackgroundGeolocation.openSettings();
        console.log("[NativeLocation] Opened Android settings via BG plugin");
        return true;
      } catch (bgError) {
        console.warn("[NativeLocation] BG openSettings failed:", bgError);
      }
    }
  } catch (error) {
    console.error("[NativeLocation] Failed to open settings:", error);
  }

  return false;
}

/**
 * Listen for app resume events (e.g. returning from Settings).
 * Returns a cleanup function.
 */
export function onAppResume(callback: () => void): () => void {
  if (!isNative()) return () => {};

  let handle: { remove: () => void } | null = null;
  let cancelled = false;

  App.addListener("resume", callback).then((h) => {
    handle = h;
    // Race guard: cleanup may have run before addListener resolved. Without
    // this the listener would survive unmount and accumulate on remount.
    if (cancelled) {
      handle.remove();
      handle = null;
    }
  });

  return () => {
    cancelled = true;
    handle?.remove();
    handle = null;
  };
}

// ─── Background Geolocation (community plugin) ─────────────────────────────

let _bgWatcherId: string | null = null;
let _currentDistanceFilter = 200; // last requested filter (for adaptive switching)

// Monotonic token identifying the current start attempt, plus a cancel flag set
// by any external stop. Together they guarantee that a watcher whose
// addWatcher() resolves after a stop (unmount, logout, POD) or after a newer
// start is removed immediately, instead of leaking a live CLLocationManager
// that nothing holds a reference to.
let _startGeneration = 0;
let _startCancelled = false;

export interface NativeWatchOptions {
  /** Distance in meters between updates. Higher = better battery. Default 200m. */
  distanceFilter?: number;
}

/**
 * Start TRUE background location tracking using @capacitor-community/background-geolocation.
 *
 * Default distanceFilter is 200m (battery-friendly). Use `restartNativeLocationWatch`
 * to switch to a tighter filter (e.g. 100m) when the driver is actively driving fast.
 *
 * On iOS: works with "When In Use" if app is in foreground.
 * For true background (locked screen), requires "Always".
 */
export async function startNativeLocationWatch(
  onPosition: (lat: number, lng: number, accuracy: number, speed?: number | null) => void,
  onError?: (error: any) => void,
  options: NativeWatchOptions = {},
): Promise<() => void> {
  if (!isNative()) {
    console.warn("[NativeLocation] startNativeLocationWatch called on web — no-op");
    return () => {};
  }

  // Claim this start synchronously (before any await) so a stop issued while
  // addWatcher() is in flight is always observed as a cancellation.
  const generation = ++_startGeneration;
  _startCancelled = false;

  // Stop any existing watch first (internal — must not cancel this start)
  await stopNativeLocationWatch({ internal: true });

  const distanceFilter = options.distanceFilter ?? 200;
  _currentDistanceFilter = distanceFilter;


  try {
    console.log(`[NativeLocation] Starting background watcher (distanceFilter=${distanceFilter}m)`);

    const timeoutMs = 10_000;
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("WATCHER_START_TIMEOUT")), timeoutMs),
    );

    const watcherId = await Promise.race([
      BackgroundGeolocation.addWatcher(
        {
          backgroundTitle: "TransportMate",
          backgroundMessage: "Tracking your delivery in progress",
          requestPermissions: false,
          stale: false,
          distanceFilter,
        },
        (position?: BGLocation, error?: CallbackError) => {
          if (error) {
            console.error("[NativeLocation] BG watcher error:", error.code, error.message);
            emitTrackingDiag("watcher_error", { error_category: categoriseTrackingError(error) });
            onError?.(error);
            return;
          }
          if (position) {
            onPosition(
              position.latitude,
              position.longitude,
              position.accuracy,
              (position as any).speed ?? null,
            );
          }
        },
      ),
      timeoutPromise,
    ]);

    // Race guard: a stop (unmount, logout, state change) or a newer start may
    // have happened while addWatcher() was in flight. In that case this watcher
    // must be torn down immediately — otherwise iOS keeps an orphaned
    // CLLocationManager running with nothing able to stop it.
    if (generation !== _startGeneration || _startCancelled) {
      console.warn("[NativeLocation] WATCHER_SUPERSEDED — removing orphaned watcher:", watcherId);
      emitTrackingDiag("watcher_superseded", { distance_filter_m: distanceFilter });
      try {
        await BackgroundGeolocation.removeWatcher({ id: watcherId });
      } catch (error) {
        console.error("[NativeLocation] Failed to remove orphaned watcher:", error);
      }
      return () => {};
    }

    _bgWatcherId = watcherId;
    console.log("[NativeLocation] WATCHER_STARTED:", { watcherId, distanceFilter });
    emitTrackingDiag("watcher_started", { distance_filter_m: distanceFilter });

    return () => stopNativeLocationWatch();
  } catch (error) {
    console.error("[NativeLocation] Failed to start background watcher:", error);
    emitTrackingDiag("watcher_start_failed", {
      distance_filter_m: distanceFilter,
      error_category: categoriseTrackingError(error),
    });
    onError?.(error);
    return () => {};
  }
}

/** Returns the current effective distanceFilter, or null if no watcher is active. */
export function getCurrentDistanceFilter(): number | null {
  return _bgWatcherId ? _currentDistanceFilter : null;
}

/**
 * Stop background location tracking.
 */
export async function stopNativeLocationWatch(
  options: { internal?: boolean } = {},
): Promise<void> {
  // Any external stop cancels an in-flight start so it cannot install a watcher
  // after us. Internal stops (issued by startNativeLocationWatch itself) must
  // not cancel the start that issued them.
  if (!options.internal) {
    _startCancelled = true;
  }

  if (_bgWatcherId !== null) {
    try {
      await BackgroundGeolocation.removeWatcher({ id: _bgWatcherId });
      console.log("[NativeLocation] WATCHER_STOPPED:", { watcherId: _bgWatcherId });
      emitTrackingDiag("watcher_stopped", { reason: options.internal ? "internal_before_start" : "external_stop" });
    } catch (error) {
      console.error("[NativeLocation] Failed to remove watcher:", error);
    }
    _bgWatcherId = null;
  }
}

// ─── Debug helpers (behind TM_DEBUG flag) ────────────────────────────────────

export interface LocationDebugState {
  permission: LocationPermissionLevel;
  cachedPermission: LocationPermissionInfo | null;
  cacheAge: number | null;
  didPrimeThisSession: boolean;
  bgWatcherId: string | null;
  bgCapabilityCache: boolean | null;
  bgCapabilityCacheAge: number | null;
  platform: string;
  isNative: boolean;
}

/**
 * Get full internal state for debug panel. Only call behind TM_DEBUG.
 */
export function getLocationDebugState(): LocationDebugState {
  return {
    permission: _cachedPermission?.permission ?? "unknown",
    cachedPermission: _cachedPermission,
    cacheAge: _cachedAt ? Date.now() - _cachedAt : null,
    didPrimeThisSession: _didPrimeThisSession,
    bgWatcherId: _bgWatcherId,
    bgCapabilityCache: _bgCapabilityCache,
    bgCapabilityCacheAge: _bgCapabilityCacheAt ? Date.now() - _bgCapabilityCacheAt : null,
    platform: platform(),
    isNative: isNative(),
  };
}

/**
 * Force-reset the prime flag (debug only — allows re-priming).
 */
export function debugResetPrimeFlag(): void {
  _didPrimeThisSession = false;
  console.log("[NativeLocation] DEBUG: Prime flag reset");
}

/**
 * Get a single position reading using Capacitor Geolocation.
 */
export async function getNativePosition(): Promise<{ lat: number; lng: number; accuracy: number } | null> {
  if (!isNative()) return null;

  try {
    const pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    });
    return {
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
    };
  } catch (error) {
    console.error("[NativeLocation] getCurrentPosition error:", error);
    return null;
  }
}
